import * as DG from "ui/dialogs"
import { ChangeEvent, useEffect, useState, FocusEvent } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import {
  createBillingSubType,
  fetchBillingSubTypes,
  patchBillingSubType,
} from "api/billingtypes"
import { queryCache, useMutation, useQuery } from "react-query"

import { BillingSubType } from "../../MarketSettings/List/BillingTypes/types"
import Button from "ui/buttons/Button"
import { Loading } from "components/Loading"
import { Switch } from "@material-ui/core"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"

// import { Checkbox } from "@material-ui/core"

export function SubBillingTypesModal() {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { get, set } = useLocationSearch()
  const billingTypeId = get("billingTypeId")

  const params = new URLSearchParams()
  !!billingTypeId && params.set("billingTypeId", billingTypeId)
  const { data, isLoading, updatedAt } = useQuery(
    ["market-billing-subtypes", params.toString()],
    fetchBillingSubTypes,
    {
      enabled: !!billingTypeId,
    },
  )

  const [options, setOptions] = useState<Partial<BillingSubType>[]>([])
  const [, setErrors] = useState<ErrorsMap | undefined>({})

  const onClose = () => {
    set("billingTypeId", undefined)
  }

  // ** create new sub-billing type
  const [create] = useMutation(createBillingSubType, {
    onSuccess: () => {
      queryCache.invalidateQueries(["market-billing-types"])
      queryCache.invalidateQueries([
        "market-billing-subtypes",
        params.toString(),
      ])
      setErrors(undefined)
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  // ** update a sub-billing type
  const [patch] = useMutation(patchBillingSubType, {
    onSuccess: () => {
      queryCache.invalidateQueries("market-billing-types")
      queryCache.invalidateQueries([
        "market-billing-subtypes",
        params.toString(),
      ])
      setErrors(undefined)
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleChange =
    (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) =>
    (index: number) => {
      const { value } = evt?.target ?? {}
      setOptions((s) => {
        let _options = [...s]
        _options.splice(index, 1, { ..._options[index], name: value })
        return _options
      })
    }

  const handleBlur =
    (id?: string) =>
    (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = evt?.target
      const pld = {
        name: value,
      } as Partial<BillingSubType>
      if (!!id) {
        !!value.trim() && patch({ ...pld, id })
      } else {
        !!value.trim() && create({ ...pld, status: "ACTIVE", billingTypeId })
      }
    }

  const handleSwitchChange =
    (name?: string, id?: string) =>
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const pld = {
        name,
        status: checked ? "ACTIVE" : "INACTIVE",
      } as Partial<BillingSubType>
      patch({ ...pld, id })
    }

  const addRow = () => {
    setOptions((s) => [...s, { name: "", status: "ACTIVE" }])
  }

  const id = "create-new-subBillingtype-dialog"

  useEffect(() => {
    if (!!data?.length) {
      if (!options.length) {
        setOptions(data)
      } else {
        const [existing, inserted] = compareSubtypeLists(data, options)
        const updatedExisting = options?.map((o) => {
          const u = existing?.find((e) => e.id === o.id)
          return { ...o, ...u }
        })
        const keepExisting = updatedExisting.filter((ue) => !!ue.id)
        setOptions([...keepExisting, ...inserted])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  useEffect(() => {
    setOptions([])
  }, [billingTypeId])

  const loading = isLoading || (!!data?.length && !options.length)

  return (
    <DG.Dialog
      fullWidth={false}
      open={!!billingTypeId}
      onClose={onClose}
      id={id}
      className={styles.dialog}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id} className="BodyBody-1BlackBold">
          {t("market.billingTypes.addSubBillingType")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent
        center
        paddingTop
        className={
          hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
            ? styles.containerWithButtons
            : styles.container
        }
      >
        {loading ? (
          <Loading />
        ) : options?.length ? (
          options.map((o, idx) => (
            <div key={idx} className={styles.inputRow}>
              <TextInput
                variant="outlined"
                color="primary"
                size="medium"
                margin="normal"
                value={o?.name || ""}
                onChange={(e) => handleChange(e)(idx)}
                onBlur={handleBlur(o?.id)}
                className={styles.input}
                disabled={!hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])}
              />
              <Switch
                checked={o?.status === "ACTIVE"}
                onChange={handleSwitchChange(o?.name, o?.id)}
                disabled={
                  !!o?.id && !hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
                }
                color={
                  hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
                    ? "primary"
                    : undefined
                }
              />
            </div>
          ))
        ) : null}
        <WithAuthority allowed={["AUTH_ACCESS_MARKET_FINANCE"]}>
          <Button
            onClick={addRow}
            color="primary"
            className={styles.textButton}
          >
            {t("market.addSubbilingTypeBtn")}
          </Button>
        </WithAuthority>
      </DG.DialogContent>
      {hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"]) ? (
        <DG.DialogFooter>
          <DG.DialogActions>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={onClose}
              disabled={!hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])}
            >
              {t("market.addSubBillingType.done")}
            </Button>
          </DG.DialogActions>
        </DG.DialogFooter>
      ) : null}
    </DG.Dialog>
  )
}

const styles = {
  container: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) var(--spacing-xs) 0;
  `,
  containerWithButtons: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) 0 0;
  `,
  inputRow: css`
    display: flex;
    padding-right: var(--spacing-xxl-7);
    align-items: center;
    justify-content: space-between;

    :first-child {
      > svg {
        display: none;
      }
    }
  `,
  input: css`
    min-width: 280px;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;

    &:focus {
      box-shadow: none;
    }
  `,
  dialog: css`
    *::-webkit-scrollbar {
      width: 6px;
      border-radius: 4px;
      background: var(--primary-light);
    }
    *::-webkit-scrollbar-thumb {
      width: 6px;
      height: var(--spacing-xl);
      border-radius: 4px;
      background: var(--primary-main);
    }
  `,
}

function equalityCheck(item: Partial<BillingSubType>) {
  return (list: Partial<BillingSubType>[]) =>
    list.some((li) => item.id === li.id)
}

function compareSubtypeLists(
  a: Partial<BillingSubType>[],
  b: Partial<BillingSubType>[],
) {
  const existing = a.filter((a) => equalityCheck(a)(b))
  const inserted = a.filter((a) => !equalityCheck(a)(b))

  return [existing, inserted]
}
